import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ScrollArea, Skeleton, SpecraButton, SpecraCard, SpecraCardContent, SpecraHeading, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../contexts/authContext';
import { projectDataClient } from '../../../../lib/connect/clients';
export function ProposalReadiness({ projectId }) {
    const { user } = useAuth();
    if (!user) return null;
    return /*#__PURE__*/ _jsx(ScopedReadiness, {
        projectId: projectId,
        principal: `${user.tenant_id}:${user.id}`
    }, `${user.tenant_id}:${user.id}:${projectId}`);
}
function ScopedReadiness({ projectId, principal }) {
    const query = useQuery({
        queryKey: [
            'proposal-readiness',
            principal,
            projectId
        ],
        queryFn: ({ signal })=>projectDataClient.getProposalHome({
                projectId
            }, {
                signal
            }),
        retry: false,
        gcTime: 0,
        refetchOnMount: 'always'
    });
    const root = Testids.proposalReadiness;
    // Recheck authority on every visit/refresh; a failed read never displays cached names/counts.
    if (query.isPending || query.isFetching || !query.isFetchedAfterMount) {
        return /*#__PURE__*/ _jsxs("section", {
            "aria-label": "Loading Proposal readiness",
            "aria-busy": "true",
            "data-loading": "true",
            "data-testid": root.loading,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Loading Proposal readiness…"
                }),
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-24 w-full"
                })
            ]
        });
    }
    if (query.isError) {
        return /*#__PURE__*/ _jsxs("section", {
            role: "alert",
            "data-testid": root.error,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Proposal readiness could not be loaded. Retry or request Proposal access."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": root.retry,
                    onClick: ()=>query.refetch(),
                    children: "Retry readiness"
                })
            ]
        });
    }
    const data = query.data;
    const today = new Date().toISOString().slice(0, 10);
    // Only normalized server dates participate in ordering. Source wording stays visible below.
    const next = data.deadlines.filter((item)=>/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(item.date) && Number.isFinite(Date.parse(item.date)) && item.date.slice(0, 10) >= today).sort((a, b)=>a.date.localeCompare(b.date))[0];
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": root.panel,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    children: "Proposal readiness"
                }),
                /*#__PURE__*/ _jsxs(SpecraText, {
                    children: [
                        "As of ",
                        new Date(data.asOf).toLocaleString(),
                        ". Counts cover the visible owning records."
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": root.refresh,
                    onClick: ()=>query.refetch(),
                    children: "Refresh readiness"
                }),
                /*#__PURE__*/ _jsxs("section", {
                    "data-testid": root.nextDeadline,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            children: "Next deadline"
                        }),
                        next ? /*#__PURE__*/ _jsx(ReadinessAction, {
                            item: next
                        }) : /*#__PURE__*/ _jsx(SpecraText, {
                            children: "No upcoming dated deadline recorded. Check source dates and any overdue items below."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    children: "Needs Attention"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Completed drafting does not establish substantive compliance, a reviewed version or submission approval."
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid items-start gap-4 md:grid-cols-3",
                    children: [
                        data.hasComplianceBasis ? /*#__PURE__*/ _jsx(ReadinessGroup, {
                            title: "Open requirements",
                            items: data.openRequirements,
                            testid: root.requirements,
                            empty: "No open requirements on the current issued source basis."
                        }) : /*#__PURE__*/ _jsxs("section", {
                            "data-testid": root.requirements,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    children: "Open requirements: source basis not established. No issued source does not mean no obligations."
                                }),
                                /*#__PURE__*/ _jsx(Link, {
                                    to: `/proposal/${projectId}/v2/sources/files`,
                                    children: "Issue or inspect solicitation"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ReadinessGroup, {
                            title: "Changed amendment",
                            items: data.changedAmendments,
                            testid: root.amendments,
                            empty: "No replacement of an earlier issued source recorded."
                        }),
                        /*#__PURE__*/ _jsx(ReadinessGroup, {
                            title: "Pending reviews",
                            items: data.pendingReviews,
                            testid: root.reviews,
                            empty: "No open review sessions recorded. This does not mean every material is reviewed."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    children: "Status and context"
                }),
                /*#__PURE__*/ _jsx(ReadinessGroup, {
                    title: "Saved deadlines",
                    items: data.deadlines,
                    testid: root.deadlines,
                    empty: "No saved deadlines. Inspect the solicitation and add milestones for this bid."
                }),
                /*#__PURE__*/ _jsx(ReadinessGroup, {
                    title: "Assigned sections",
                    items: data.assignments,
                    testid: root.assignments,
                    empty: "No section writer or reviewer assignments recorded."
                }),
                /*#__PURE__*/ _jsx(ReadinessGroup, {
                    title: "Volumes",
                    items: data.volumes,
                    testid: root.volumes,
                    empty: "No response volumes recorded. Create the response structure before drafting."
                }),
                /*#__PURE__*/ _jsxs("section", {
                    "data-testid": root.packageContext,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Inspect pricing evidence in Pricing. Check final response documents and submission checklists in each volume above; no package approval is inferred here."
                        }),
                        /*#__PURE__*/ _jsx(Link, {
                            to: `/proposal/${projectId}/v2/sources/pricing`,
                            children: "Inspect pricing and version evidence"
                        }),
                        ' · ',
                        /*#__PURE__*/ _jsx(Link, {
                            to: `/proposal/${projectId}/v2/sources/files`,
                            children: "Inspect source files and amendment impacts"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(ReadinessGroup, {
                    title: "Selected strategy context",
                    items: data.strategy,
                    testid: root.strategy,
                    empty: "No selected strategy context available with your current source permissions."
                })
            ]
        })
    });
}
function ReadinessGroup({ title, items, testid, empty }) {
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": testid,
        "data-count": items.length,
        className: "min-w-0 space-y-2",
        children: [
            /*#__PURE__*/ _jsxs(SpecraHeading, {
                children: [
                    title,
                    " (",
                    items.length,
                    ")"
                ]
            }),
            items.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                children: empty
            }),
            items.length > 3 ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        children: [
                            "Scroll to inspect all ",
                            items.length,
                            " records."
                        ]
                    }),
                    /*#__PURE__*/ _jsx("section", {
                        "aria-label": `${title}: all ${items.length} records`,
                        children: /*#__PURE__*/ _jsx(ScrollArea, {
                            className: "h-48",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "space-y-2 pr-4",
                                children: items.map((item)=>/*#__PURE__*/ _jsx(ReadinessAction, {
                                        item: item
                                    }, item.id))
                            })
                        })
                    })
                ]
            }) : items.map((item)=>/*#__PURE__*/ _jsx(ReadinessAction, {
                    item: item
                }, item.id))
        ]
    });
}
function ReadinessAction({ item }) {
    return /*#__PURE__*/ _jsxs("article", {
        "data-testid": Testids.proposalReadiness.item,
        "data-record-id": item.id,
        className: "space-y-1 break-words border-b border-border py-2 last:border-b-0",
        children: [
            /*#__PURE__*/ _jsx(Link, {
                className: "block break-words text-primary underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring",
                to: item.href,
                onClick: ()=>{
                    const anchor = item.href.split('#')[1];
                    if (anchor) document.getElementById(anchor)?.scrollIntoView({
                        block: 'start'
                    });
                },
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: item.title || 'Open owning record'
                })
            }),
            item.date && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: item.date
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                tone: "secondary",
                children: item.explanation
            })
        ]
    });
}
